import { useEffect, useState } from "react";
import { Keyboard, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Student-controlled toggles for special keys during Legal typing. Matches the
 * restrictions students face in real steno exams (some boards disable Ctrl,
 * function keys, or extra spaces). Persists to localStorage so a student's
 * preferred exam profile survives reloads.
 *
 * Emits: { capsLockWarning, spaceBar, controlKeys, functionKeys }
 *
 * Toggle semantics (ON = allowed / shown, OFF = blocked / hidden):
 *   - capsLockWarning: show the "Caps Lock ON" pill from RichTextEditor
 *   - spaceBar: allow pressing Space (exam boards that disallow double-spacing
 *     can simulate a locked spacebar drill)
 *   - controlKeys: allow Ctrl / Cmd combinations (Ctrl+B/I/U formatting shortcuts,
 *     Ctrl+C/V paste, etc.)
 *   - functionKeys: allow F1–F12 (some browsers hijack F5/F11 which can wreck
 *     an exam attempt)
 */
export const DEFAULT_KEY_CONTROLS = {
  capsLockWarning: true,
  spaceBar: true,
  controlKeys: true,
  functionKeys: true,
};

const LABELS = [
  { id: "capsLockWarning", label: "Caps Lock warning", hint: "Show a pill when Caps Lock is ON" },
  { id: "spaceBar",        label: "Space bar",         hint: "Turn off to lock the spacebar during drills" },
  { id: "controlKeys",     label: "Control shortcuts", hint: "Ctrl/Cmd + B / I / U / C / V — off blocks all Ctrl combos" },
  { id: "functionKeys",    label: "Function keys",     hint: "F1–F12 — off prevents F5 reloads mid-exam" },
];

const STORAGE_KEY = "stenosync.keyControls.v1";

export function loadKeyControls() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_KEY_CONTROLS };
    return { ...DEFAULT_KEY_CONTROLS, ...JSON.parse(raw) };
  } catch { return { ...DEFAULT_KEY_CONTROLS }; }
}

function saveKeyControls(v) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}

export default function KeyControlsPanel({ value, onChange, testId = "key-controls" }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(value || DEFAULT_KEY_CONTROLS);

  useEffect(() => { setLocal(value || DEFAULT_KEY_CONTROLS); }, [value]);

  const toggle = (id) => {
    const next = { ...local, [id]: !local[id] };
    setLocal(next);
    saveKeyControls(next);
    onChange?.(next);
  };

  const activeCount = Object.values(local).filter(Boolean).length;

  return (
    <div className="border-2 border-border rounded-xl bg-secondary/40" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <Keyboard className="w-4 h-4 text-brand-gold" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold">
            Key controls
          </span>
          <span className="text-xs font-mono text-muted-foreground">
            {activeCount}/4 enabled
          </span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {LABELS.map((l) => {
            const on = !!local[l.id];
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => toggle(l.id)}
                data-testid={`${testId}-${l.id}`}
                aria-pressed={on}
                title={l.hint}
                className={`flex items-start gap-3 px-3 py-2.5 border-2 rounded-lg text-left transition-colors ${
                  on
                    ? "border-brand-emerald/40 bg-brand-emerald/10"
                    : "border-brand-crimson/40 bg-brand-crimson/10"
                }`}
              >
                <span
                  className={`mt-0.5 inline-flex w-9 h-5 rounded-full transition-colors ${
                    on ? "bg-brand-emerald" : "bg-brand-crimson"
                  }`}
                >
                  <span
                    className={`inline-block w-4 h-4 bg-white rounded-full mt-0.5 transition-transform ${
                      on ? "translate-x-4" : "translate-x-1"
                    }`}
                  />
                </span>
                <span className="flex-1">
                  <span className={`block text-xs font-bold uppercase tracking-widest ${on ? "text-brand-emerald" : "text-brand-crimson"}`}>
                    {l.label} · {on ? "ON" : "OFF"}
                  </span>
                  <span className="block text-[10px] font-mono text-muted-foreground mt-0.5">{l.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
